/**
 * ConfigGeneratorPage — pick a template + fill out a schema-driven form +
 * preview the rendered config + copy to clipboard.
 *
 * Two-pane layout:
 *   - Left: template gallery, filterable by vendor + category + free-text
 *   - Right: when a template is selected, a dynamic form (driven by the
 *     template's `fields[]`) and a live preview of the rendered output.
 *
 * Template body uses simple `{{name}}` substitution and `{{#flag}}…{{/flag}}`
 * for boolean toggles — kept intentionally minimal so the renderer stays in
 * one ~30-line function.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCopy,
  Search,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { FormSkeleton } from "@/components/ui/skeleton";
import { useConfigTemplates } from "@/stores/config-templates";
import type {
  ConfigTemplate,
  TemplateField,
} from "@/api/schemas/config-templates";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/** Render a template body by substituting `{{field}}` and toggling `{{#flag}}…{{/flag}}`. */
function renderTemplate(
  body: string,
  values: Record<string, string | number | boolean>,
): string {
  // 1. Boolean section toggles. Keep contents only when value is truthy.
  let out = body.replace(
    /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
    (_, key: string, inner: string) => {
      return values[key] ? inner : "";
    },
  );
  // 2. Plain `{{key}}` substitution. Falls back to "<unset>" so missing
  //    placeholders are visually obvious in the preview.
  out = out.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const v = values[key];
    if (v === undefined || v === null || v === "") return "<unset>";
    return String(v);
  });
  return out;
}

export function ConfigGeneratorPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useConfigTemplates();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");
  const [vendorFilter, setVendorFilter] = useState<string>("all");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((t) => {
      if (vendorFilter !== "all" && t.vendor !== vendorFilter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [data, query, vendorFilter]);

  // Selected template + working values for its fields.
  const [selected, setSelected] = useState<ConfigTemplate | null>(null);
  const [values, setValues] = useState<
    Record<string, string | number | boolean>
  >({});

  function selectTemplate(t: ConfigTemplate | null) {
    setSelected(t);
    const init: Record<string, string | number | boolean> = {};
    if (t) {
      for (const f of t.fields) {
        init[f.name] =
          f.defaultValue ??
          (f.type === "number" ? 0 : f.type === "boolean" ? false : "");
      }
    }
    setValues(init);
  }

  const rendered = useMemo(() => {
    if (!selected) return "";
    return renderTemplate(selected.body, values);
  }, [selected, values]);

  // Group templates by vendor for the gallery — even when the filter is "all".
  const grouped = useMemo(() => {
    const out: Record<string, ConfigTemplate[]> = {};
    for (const t of visible) (out[t.vendor] ??= []).push(t);
    return out;
  }, [visible]);

  function copyRendered() {
    if (!rendered) return;
    void navigator.clipboard?.writeText(rendered);
    toast.success("Rendered config copied");
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Config generator</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Config generator
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Pick a blessed template, fill in the fields, and copy the rendered
            config. Templates are versioned in the backend; the GuestTek-blessed
            ones are marked.
          </p>
        </div>
        <Badge variant="info" size="md">
          <Sparkles size={11} />
          {data.length} templates
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[420px_1fr]">
        {/* Gallery */}
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div>
              <CardTitle>Templates</CardTitle>
              <CardSub>
                {visible.length} of {data.length} shown
              </CardSub>
            </div>
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter templates…"
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(["all", "meraki", "aruba", "system"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVendorFilter(v)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                    vendorFilter === v
                      ? "border-violet/40 bg-aurora-soft text-violet"
                      : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>

            {status === "loading" && data.length === 0 ? (
              <FormSkeleton lines={4} />
            ) : visible.length === 0 ? (
              <div className="rounded-xl border border-line bg-surface p-5 text-center text-sm text-fg-mute">
                No templates match.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {Object.entries(grouped).map(([vendor, templates]) => (
                  <div key={vendor} className="flex flex-col gap-1.5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                      {vendor}
                    </div>
                    {templates.map((t) => {
                      const isSelected = selected?.slug === t.slug;
                      return (
                        <button
                          key={t.slug}
                          type="button"
                          onClick={() => selectTemplate(t)}
                          className={cn(
                            "flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors",
                            isSelected
                              ? "border-violet/50 bg-aurora-soft"
                              : "border-line bg-surface hover:bg-surface-2",
                          )}
                        >
                          <div className="flex w-full items-center justify-between gap-2">
                            <div className="truncate text-sm font-semibold text-fg">
                              {t.name}
                            </div>
                            {t.blessed ? (
                              <Badge variant="ok" size="sm">
                                <CheckCircle2 size={10} />
                                blessed
                              </Badge>
                            ) : null}
                          </div>
                          <div className="font-mono text-[10.5px] text-fg-mute">
                            {t.slug} · {t.category}
                          </div>
                          <div className="mt-0.5 line-clamp-2 text-[11px] text-fg-dim">
                            {t.description}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form + preview */}
        {selected ? (
          <div className="flex flex-col gap-4">
            <Card>
              <CardContent className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>{selected.name}</CardTitle>
                    <CardSub className="mt-1 font-mono">
                      {selected.slug} · {selected.category}
                    </CardSub>
                  </div>
                  <Badge variant="neutral" size="sm">
                    {selected.vendor}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {selected.fields.map((f) => (
                    <FieldInput
                      key={f.name}
                      field={f}
                      value={values[f.name]}
                      onChange={(v) =>
                        setValues((prev) => ({ ...prev, [f.name]: v }))
                      }
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
                <div>
                  <CardTitle>Rendered preview</CardTitle>
                  <CardSub className="mt-1">
                    Updates live as you edit fields above.
                  </CardSub>
                </div>
                <Button size="sm" onClick={copyRendered}>
                  <ClipboardCopy size={13} />
                  Copy
                </Button>
              </div>
              <pre className="m-0 max-h-[320px] overflow-auto bg-bg-0 p-5 font-mono text-[12px] leading-relaxed text-fg">
                {rendered}
              </pre>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
              <Sparkles size={20} className="text-violet" />
              <CardTitle>Pick a template</CardTitle>
              <CardSub>Select one on the left to start filling it out.</CardSub>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ---------- Single field input ---------- */

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string | number | boolean | undefined;
  onChange: (v: string | number | boolean) => void;
}) {
  if (field.type === "boolean") {
    return (
      <label className="flex items-start gap-2 rounded-xl border border-line bg-surface p-3 text-sm">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 accent-violet"
        />
        <div>
          <div className="font-semibold text-fg">{field.label}</div>
          {field.hint ? (
            <div className="text-[11px] text-fg-mute">{field.hint}</div>
          ) : null}
        </div>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`f-${field.name}`}>
          {field.label}
          {field.required ? <span className="text-rose">*</span> : null}
        </Label>
        <select
          id={`f-${field.name}`}
          aria-label={field.label}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
        >
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {field.hint ? (
          <span className="text-[11px] text-fg-mute">{field.hint}</span>
        ) : null}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-1.5 lg:col-span-2">
        <Label htmlFor={`f-${field.name}`}>
          {field.label}
          {field.required ? <span className="text-rose">*</span> : null}
        </Label>
        <textarea
          id={`f-${field.name}`}
          aria-label={field.label}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="rounded-xl border border-line bg-surface px-3 py-2 font-mono text-xs text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
        />
        {field.hint ? (
          <span className="text-[11px] text-fg-mute">{field.hint}</span>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`f-${field.name}`}>
        {field.label}
        {field.required ? <span className="text-rose">*</span> : null}
      </Label>
      <Input
        id={`f-${field.name}`}
        type={field.type === "number" ? "number" : "text"}
        value={String(value ?? "")}
        onChange={(e) =>
          onChange(
            field.type === "number" ? Number(e.target.value) : e.target.value,
          )
        }
      />
      {field.hint ? (
        <span className="text-[11px] text-fg-mute">{field.hint}</span>
      ) : null}
    </div>
  );
}
